import type { SimpleChanges } from '@angular/core';

/** Immutable values safe to hand out by reference as a shared default. */
type InputDefaultPrimitive = string | number | boolean | bigint | symbol | null;

/** Names of `@InputDefault` properties, stored per class prototype (symbol so it stays off Object.keys / Angular reflection). */
const DECORATED = Symbol('inputDefault.properties');
/** Marks a prototype whose `ngOnChanges` has already been wrapped, so we only wrap once per class. */
const WRAPPED = Symbol('inputDefault.ngOnChangesWrapped');

interface DecoratedPrototype {
  [DECORATED]?: Set<string>;
  [WRAPPED]?: boolean;
  ngOnChanges?(changes: SimpleChanges): void;
}

/**
 * Builds the coalescing function: returns the bound value unless it is
 * `undefined`, in which case it returns the default. A factory is invoked per
 * call so each coalesced value is a fresh copy.
 */
function coalescer<T>(defaultValueOrFactory: T | (() => T)): (value: T | undefined) => T {
  return (value: T | undefined): T =>
    value !== undefined
      ? value
      : typeof defaultValueOrFactory === 'function'
        ? (defaultValueOrFactory as () => T)()
        : defaultValueOrFactory;
}

/**
 * Wrap the prototype's `ngOnChanges` once so that, for every decorated property
 * whose binding resolved to `undefined`, `SimpleChange.currentValue` is patched
 * to the coalesced value the getter returns. Angular records the raw bound value
 * before the setter runs, so without this the body would still see `undefined`.
 *
 * Only effective when the component statically declares `ngOnChanges` — Angular
 * wires the OnChanges hook at compile time and invokes `this.ngOnChanges`
 * dynamically at runtime, which is why replacing it here is picked up.
 */
function wrapOnChanges(prototype: DecoratedPrototype): void {
  const original = prototype.ngOnChanges;

  prototype.ngOnChanges = function (this: DecoratedPrototype & Record<string, unknown>, changes: SimpleChanges): void {
    const properties = this[DECORATED];
    if (properties) {
      for (const key of Object.keys(changes)) {
        if (properties.has(key) && changes[key].currentValue === undefined) {
          changes[key].currentValue = this[key]; // getter returns the coalesced default
        }
      }
    }
    original?.call(this, changes);
  };
}

/**
 * Gives an `@Input()` a default value that an `undefined` binding cannot clobber.
 *
 * Field initializers only run at construction, so `[x]="maybeUndefined"` wipes
 * them. This installs a coalescing get/set accessor — `this.x` is always the
 * default while the binding is `undefined` — and wraps the component's
 * `ngOnChanges` (once) so `changes.x.currentValue` reports the same default.
 *
 * Apply it next to `@Input()`; the field initializer is then redundant. The
 * component must declare `ngOnChanges` for the `currentValue` patch to apply
 * (`this.x` coalescing works regardless). Mutable defaults must be passed as a
 * factory (the type guard enforces it) so each instance gets its own copy.
 *
 * @example
 * \@Input() \@InputDefault(false) loading: boolean;
 * \@Input() \@InputDefault(() => []) items: Item[];
 */
export function InputDefault<T extends InputDefaultPrimitive>(value: T): PropertyDecorator;
export function InputDefault<T>(factory: () => T): PropertyDecorator;
export function InputDefault<T>(defaultValueOrFactory: T | (() => T)): PropertyDecorator {
  const coalesce = coalescer(defaultValueOrFactory);

  return (target: object, propertyKey: string | symbol): void => {
    const prototype = target as DecoratedPrototype;
    const key = propertyKey as string;
    const store = Symbol(`inputDefault:${key}`);

    // Own Set per class; seed from the inherited one so a subclass keeps its ancestors' props
    // without mutating their Set.
    if (!Object.prototype.hasOwnProperty.call(prototype, DECORATED)) {
      prototype[DECORATED] = new Set(prototype[DECORATED]);
    }
    prototype[DECORATED]!.add(key);

    Object.defineProperty(prototype, key, {
      get(this: Record<symbol, T>): T {
        if (!(store in this)) {
          this[store] = coalesce(undefined);
        }
        return this[store];
      },
      set(this: Record<symbol, T>, value: T | undefined): void {
        this[store] = coalesce(value);
      },
      enumerable: true,
      configurable: true,
    });

    if (prototype.ngOnChanges && !Object.prototype.hasOwnProperty.call(prototype, WRAPPED)) {
      prototype[WRAPPED] = true;
      wrapOnChanges(prototype);
    }
  };
}
